import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Search,
  Filter,
  Battery as BatteryIcon,
  Sun as SunIcon,
  DollarSign as DollarSignIcon,
} from 'lucide-react';
import { getSites, createSite, updateSite, deleteSite, getUsers, getSiteCredentials } from '../api/client';
import type { SiteCredentials } from '../api/client';
import { Pagination } from '../components/Pagination';
import { useToast, apiErrorMessage } from '../components/Toast';
import { SectionHeader, CredentialField, formatCredValue, SOLAR_INVERTERS, SOLAR_CONTROL_FIELDS } from '../components/site/SiteFormHelpers';
import type { SiteDetail, SiteCreate, SiteUpdate, User } from '../types';

const ITEMS_PER_PAGE = 10;

// Available options for dropdowns
const PRICE_PROVIDERS = ['amber', 'aemo', 'simulated', 'globird_zerohero', 'flowpower_happy_hour'];
const FORECAST_PROVIDERS = ['open_meteo', 'solcast', 'forecast_solar', 'simulated'];
const OPTIMIZER_ENGINES = [
  { value: 'emhass', label: 'SolanIQ Optimizer' },
  { value: 'simple_rule', label: 'Simple Rule' },
];
const BATTERY_ADAPTERS = [
  { value: 'esy_sunhome', label: 'ESY Sunhome', comingSoon: false },
  { value: 'demo', label: 'Demo/Simulation Mode', comingSoon: false },
  { value: 'alpha_ess', label: 'AlphaESS', comingSoon: true },
  { value: 'byd', label: 'BYD Battery', comingSoon: true },
  { value: 'tesla_gateway', label: 'Tesla Powerwall', comingSoon: true },
];
const REGIONS = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

// `state` is only filled in when the address autocomplete supplied it (sometimes
// as the full name), but every site has a NEM region ("NSW1"), so the state
// filter falls back to that.
const STATE_CODES: Record<string, string> = {
  'new south wales': 'NSW',
  'victoria': 'VIC',
  'queensland': 'QLD',
  'south australia': 'SA',
  'western australia': 'WA',
  'tasmania': 'TAS',
  'northern territory': 'NT',
  'australian capital territory': 'ACT',
};
const siteStateCode = (site: SiteDetail): string => {
  const state = site.state?.trim();
  if (state) return STATE_CODES[state.toLowerCase()] || state.toUpperCase();
  return site.region.replace(/\d+$/, '');
};

// Simulation scenarios available for testing
const SIMULATION_SCENARIOS: { id: string; name: string; description: string }[] = [
  { id: 'sunny_day_tou', name: 'Sunny Day with TOU', description: 'Perfect solar day with time-of-use pricing. Tests basic arbitrage.' },
  { id: 'cloudy_day_volatile', name: 'Cloudy Day + Volatile Prices', description: 'Overcast with wholesale price volatility. Tests forecast uncertainty.' },
  { id: 'solar_sponge', name: 'Solar Sponge Pricing', description: 'Negative midday prices, high evening. Tests grid charging during surplus.' },
  { id: 'extreme_event', name: 'Extreme Price Event', description: 'Major price volatility with spikes. Stress tests safety limits.' },
  { id: 'ev_overnight', name: 'EV Charging Overnight', description: 'Home with EV charging overnight. Tests off-peak arbitrage.' },
  { id: 'small_system', name: 'Small System', description: '5kW solar with 5kWh battery. Tests constraint handling.' },
  { id: 'commercial', name: 'Commercial Site', description: 'Commercial 9-5 load pattern with large system.' },
];

// Provider credential requirements
const PRICE_PROVIDER_FIELDS: Record<string, { label: string; key: string; type: string; placeholder: string; required?: boolean }[]> = {
  amber: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'Enter your Amber API key', required: true },
  ],
  localvolts: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'Enter your LocalVolts API key', required: true },
    { label: 'Partner ID', key: 'partner_id', type: 'text', placeholder: 'Optional partner ID' },
  ],
  aemo: [], // No credentials needed - free public API
  simulated: [], // No credentials - uses scenario config
};

const FORECAST_PROVIDER_FIELDS: Record<string, { label: string; key: string; type: string; placeholder: string; required?: boolean }[]> = {
  solcast: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'Enter your Solcast API key', required: true },
    { label: 'Resource ID', key: 'resource_id', type: 'text', placeholder: 'Your Solcast rooftop resource ID', required: true },
  ],
  open_meteo: [], // Keyless weather-based forecast - uses lat/long + solar_config
  forecast_solar: [], // Uses lat/long, no API key needed
  simulated: [], // No credentials - uses scenario config
};

const BATTERY_ADAPTER_FIELDS: Record<string, { label: string; key: string; type: string; placeholder: string; required?: boolean }[]> = {
  esy_sunhome: [
    { label: 'Username', key: 'username', type: 'text', placeholder: 'ESY app username or email', required: true },
    { label: 'Password', key: 'password', type: 'password', placeholder: 'ESY app password', required: true },
  ],
  demo: [], // No credentials - uses scenario config
  alpha_ess: [
    { label: 'API Key', key: 'api_key', type: 'password', placeholder: 'AlphaESS API key', required: true },
    { label: 'Serial Number', key: 'serial_number', type: 'text', placeholder: 'Device serial number', required: true },
  ],
  byd: [
    { label: 'Host / IP Address', key: 'host', type: 'text', placeholder: 'e.g. 192.168.1.100', required: true },
    { label: 'Username', key: 'username', type: 'text', placeholder: 'BYD username' },
    { label: 'Password', key: 'password', type: 'password', placeholder: 'BYD password' },
  ],
  tesla_gateway: [
    { label: 'Email', key: 'email', type: 'email', placeholder: 'Tesla account email', required: true },
    { label: 'Refresh Token', key: 'refresh_token', type: 'password', placeholder: 'Tesla API refresh token', required: true },
  ],
};

export function AdminSites() {
  const [sites, setSites] = useState<SiteDetail[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteDetail | null>(null);
  const [formData, setFormData] = useState<Partial<SiteCreate & SiteUpdate>>({});
  const [saving, setSaving] = useState(false);
  // Which collapsible section is open in the modal (only one at a time keeps the
  // dialog short). "battery" is open by default since it's the most-edited.
  type SectionKey = 'battery' | 'solar' | 'price' | null;
  const [openSection, setOpenSection] = useState<SectionKey>('battery');

  // Lazily-loaded current credentials shown via the per-field "Show current"
  // toggle. Fetched on first reveal so they don't ride along on every site
  // listing response.
  const [currentCreds, setCurrentCreds] = useState<SiteCredentials | null>(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set());

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  // Search, filters and pagination. Inactive sites are hidden unless asked for.
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Helper to get user email by ID (defined before useMemo that uses it)
  const getUserEmail = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user?.email || userId;
  };

  const availableStates = useMemo(
    () => Array.from(new Set(sites.map(siteStateCode))).sort(),
    [sites]
  );
  const inactiveCount = useMemo(() => sites.filter((site) => !site.active).length, [sites]);

  // Filter and paginate sites
  const matchingSites = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return sites.filter((site) => {
      if (stateFilter && siteStateCode(site) !== stateFilter) return false;
      if (!query) return true;
      return (
        site.name.toLowerCase().includes(query) ||
        site.region.toLowerCase().includes(query) ||
        site.city?.toLowerCase().includes(query) ||
        site.postcode?.toLowerCase().includes(query) ||
        getUserEmail(site.user_id).toLowerCase().includes(query)
      );
    });
  }, [sites, searchQuery, stateFilter, users]);

  const filteredSites = useMemo(
    () => (showInactive ? matchingSites : matchingSites.filter((site) => site.active)),
    [matchingSites, showInactive]
  );
  const hiddenInactiveCount = matchingSites.length - filteredSites.length;
  const activeFilterCount = (showInactive ? 1 : 0) + (stateFilter ? 1 : 0);

  const totalPages = Math.ceil(filteredSites.length / ITEMS_PER_PAGE);
  const paginatedSites = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredSites.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSites, currentPage]);

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, showInactive, stateFilter]);

  // Deactivating or deleting the last row of the last page would otherwise
  // leave an empty page behind.
  useEffect(() => {
    if (totalPages > 0 && currentPage >= totalPages) setCurrentPage(totalPages - 1);
  }, [currentPage, totalPages]);

  // Close the filter popover on outside click / Escape
  useEffect(() => {
    if (!showFilters) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!filtersRef.current?.contains(e.target as Node)) setShowFilters(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowFilters(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showFilters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [sitesData, usersData] = await Promise.all([getSites(), getUsers()]);
      setSites(sitesData);
      setUsers(usersData);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = () => {
    setEditingSite(null);
    setFormData({
      user_id: users[0]?.id || '',
      name: '',
      region: 'NSW',
      address_line1: '',
      address_line2: '',
      city: '',
      state: 'NSW',
      postcode: '',
      country: 'Australia',
      price_provider: 'aemo',
      price_provider_credentials: {},
      forecast_provider: 'solcast',
      forecast_provider_credentials: {},
      optimizer_engine: 'emhass',
      battery_adapter: 'esy_sunhome',
      battery_credentials: {},
      battery_config: {
        capacity_wh: 10000,
        max_charge_w: 5000,
        max_discharge_w: 5000,
      },
      solar_control_adapter: '',
      solar_credentials: {},
      solar_config: {
        capacity_kw: 6.6,
        brand: '',
        panels: 0,
        inverter: '',
      },
      simulation_config: {
        scenario: 'sunny_day_tou',
      },
    });
    setOpenSection('battery');
    setCurrentCreds(null);
    setRevealedFields(new Set());
    setShowModal(true);
  };

  const handleEdit = (site: SiteDetail) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      region: site.region,
      address_line1: site.address_line1 || '',
      address_line2: site.address_line2 || '',
      city: site.city || '',
      state: site.state || '',
      postcode: site.postcode || '',
      country: site.country || 'Australia',
      price_provider: site.price_provider,
      price_provider_credentials: {}, // Empty - credentials not returned for security
      forecast_provider: site.forecast_provider,
      forecast_provider_credentials: {}, // Empty - credentials not returned for security
      optimizer_engine: site.optimizer_engine,
      battery_adapter: site.battery_adapter,
      battery_credentials: {}, // Empty - credentials not returned for security
      battery_config: site.battery_config || {},
      solar_control_adapter: site.solar_control_adapter || '',
      solar_credentials: {}, // Empty - credentials not returned for security
      solar_config: site.solar_config || {},
      simulation_config: site.simulation_config || { scenario: 'sunny_day_tou' },
      active: site.active,
    });
    setOpenSection('battery');
    setCurrentCreds(null);
    setRevealedFields(new Set());
    setShowModal(true);
  };

  // Lazy-load credentials the first time the admin clicks "Show current" on
  // any field; subsequent clicks just toggle visibility.
  const ensureCredentialsLoaded = async () => {
    if (!editingSite || currentCreds || credsLoading) return;
    setCredsLoading(true);
    try {
      const data = await getSiteCredentials(editingSite.id);
      setCurrentCreds(data);
    } catch (err) {
      console.error(err);
      toast.error(apiErrorMessage(err, 'Failed to load current credentials'));
    } finally {
      setCredsLoading(false);
    }
  };

  const toggleRevealed = async (fieldId: string) => {
    if (revealedFields.has(fieldId)) {
      const next = new Set(revealedFields);
      next.delete(fieldId);
      setRevealedFields(next);
      return;
    }
    await ensureCredentialsLoaded();
    setRevealedFields(new Set(revealedFields).add(fieldId));
  };

  type CredsBucket = 'battery' | 'price' | 'forecast' | 'solar';
  const getCurrentCredValue = (bucket: CredsBucket, key: string): unknown => {
    if (!currentCreds) return undefined;
    const map = {
      battery: currentCreds.battery_credentials,
      price: currentCreds.price_provider_credentials,
      forecast: currentCreds.forecast_provider_credentials,
      solar: currentCreds.solar_credentials,
    } as const;
    return map[bucket]?.[key];
  };

  // Helper to check if credentials object has any non-empty values
  const hasCredentials = (creds: Record<string, unknown> | undefined): boolean => {
    if (!creds) return false;
    return Object.values(creds).some((v) => v !== '' && v !== null && v !== undefined);
  };

  // Check if any provider is using simulation
  const isUsingSimulation = () => {
    return (
      formData.price_provider === 'simulated' ||
      formData.forecast_provider === 'simulated' ||
      formData.battery_adapter === 'simulated'
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingSite) {
        // Update existing site
        const updateData: SiteUpdate = {
          name: formData.name,
          region: formData.region,
          address_line1: formData.address_line1,
          address_line2: formData.address_line2,
          city: formData.city,
          state: formData.state,
          postcode: formData.postcode,
          country: formData.country,
          price_provider: formData.price_provider,
          forecast_provider: formData.forecast_provider,
          optimizer_engine: formData.optimizer_engine,
          battery_adapter: formData.battery_adapter,
          battery_config: formData.battery_config,
          solar_control_adapter: formData.solar_control_adapter || null,
          solar_config: formData.solar_config,
          active: formData.active,
        };
        // Only include credentials if they have values (don't overwrite existing with empty)
        if (hasCredentials(formData.price_provider_credentials)) {
          updateData.price_provider_credentials = formData.price_provider_credentials;
        }
        if (hasCredentials(formData.forecast_provider_credentials)) {
          updateData.forecast_provider_credentials = formData.forecast_provider_credentials;
        }
        if (hasCredentials(formData.battery_credentials)) {
          updateData.battery_credentials = formData.battery_credentials;
        }
        if (formData.solar_control_adapter && hasCredentials(formData.solar_credentials)) {
          updateData.solar_credentials = formData.solar_credentials;
        }
        // Include simulation config if using simulated providers
        if (isUsingSimulation()) {
          updateData.simulation_config = formData.simulation_config;
        }
        await updateSite(editingSite.id, updateData);
      } else {
        // Create new site
        const createData: SiteCreate = {
          user_id: formData.user_id!,
          name: formData.name!,
          region: formData.region!,
          address_line1: formData.address_line1,
          address_line2: formData.address_line2,
          city: formData.city,
          state: formData.state,
          postcode: formData.postcode,
          country: formData.country,
          price_provider: formData.price_provider,
          price_provider_credentials: formData.price_provider_credentials,
          forecast_provider: formData.forecast_provider,
          forecast_provider_credentials: formData.forecast_provider_credentials,
          optimizer_engine: formData.optimizer_engine,
          battery_adapter: formData.battery_adapter!,
          battery_credentials: formData.battery_credentials,
          battery_config: formData.battery_config,
          solar_control_adapter: formData.solar_control_adapter || undefined,
          solar_credentials: hasCredentials(formData.solar_credentials)
            ? formData.solar_credentials
            : undefined,
          solar_config: formData.solar_config,
          simulation_config: isUsingSimulation() ? formData.simulation_config : undefined,
        };
        await createSite(createData);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      toast.error(apiErrorMessage(err, editingSite ? 'Failed to update site' : 'Failed to create site'));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (siteId: string) => {
    setDeleting(true);
    try {
      await deleteSite(siteId);
      setDeleteConfirm(null);
      toast.success('Site deleted');
      loadData();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete site'));
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (site: SiteDetail) => {
    try {
      await updateSite(site.id, { active: !site.active });
      if (site.active && !showInactive) {
        toast.info(`${site.name} is now inactive and hidden. Use the filter to show inactive sites.`);
      }
      loadData();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update site'));
      console.error(err);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-brand-orange" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Site Management
            </h1>
          </div>
          <button
            onClick={handleCreate}
            disabled={users.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Add Site
          </button>
        </div>
        {users.length === 0 && !loading && (
          <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
            Create a user first before adding sites.
          </p>
        )}
      </div>

      {/* Search Bar + Filters */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, region, city, postcode, or owner..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="relative" ref={filtersRef}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Filters"
              aria-expanded={showFilters}
              title="Filters"
              className={`relative p-2.5 border rounded-lg bg-card dark:bg-gray-700 transition-colors ${
                activeFilterCount > 0
                  ? 'border-brand-orange text-brand-orange'
                  : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Filter className="w-5 h-5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[1.125rem] h-[1.125rem] px-1 flex items-center justify-center rounded-full bg-brand-orange text-white text-[10px] font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilters && (
              <div className="absolute right-0 top-full mt-2 w-64 z-20 p-4 space-y-4 card">
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </p>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      className="w-4 h-4 text-brand-orange border-gray-300 rounded focus:ring-brand-orange"
                    />
                    Show inactive sites
                    <span className="ml-auto text-xs text-gray-400">{inactiveCount}</span>
                  </label>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    State
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {[null, ...availableStates].map((state) => (
                      <button
                        key={state ?? 'all'}
                        onClick={() => setStateFilter(state)}
                        aria-pressed={stateFilter === state}
                        className={`px-3 py-1 rounded-lg text-xs font-medium ${
                          stateFilter === state
                            ? 'bg-ink text-on-ink'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {state ?? 'All'}
                      </button>
                    ))}
                  </div>
                </div>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setShowInactive(false);
                      setStateFilter(null);
                    }}
                    className="text-xs text-brand-orange hover:underline"
                  >
                    Reset filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {(searchQuery || stateFilter || hiddenInactiveCount > 0) && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {(searchQuery || stateFilter) && (
              <>
                Found {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''}
                {searchQuery && ` matching "${searchQuery}"`}
                {stateFilter && ` in ${stateFilter}`}.{' '}
              </>
            )}
            {hiddenInactiveCount > 0 && (
              <>
                {hiddenInactiveCount} inactive site{hiddenInactiveCount !== 1 ? 's' : ''} hidden.{' '}
                <button
                  onClick={() => setShowInactive(true)}
                  className="text-brand-orange hover:underline"
                >
                  Show
                </button>
              </>
            )}
          </p>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-orange animate-spin" />
        </div>
      ) : (
        /* Sites Table */
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Site
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Solar / Storage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedSites.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {searchQuery
                      ? `No sites found matching "${searchQuery}".`
                      : stateFilter || hiddenInactiveCount > 0
                        ? 'No sites match the current filters.'
                        : 'No sites found. Click "Add Site" to create one.'}
                  </td>
                </tr>
              ) : (
                paginatedSites.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {site.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {site.region} | {OPTIMIZER_ENGINES.find(e => e.value === site.optimizer_engine)?.label || site.optimizer_engine}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {getUserEmail(site.user_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {site.solar_config?.capacity_kw
                          ? `${site.solar_config.capacity_kw} kW`
                          : '0 kW'}{' '}
                        /{' '}
                        {site.battery_config?.capacity_wh
                          ? `${(site.battery_config.capacity_wh as number) / 1000} kWh`
                          : '0 kWh'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {BATTERY_ADAPTERS.find((b) => b.value === site.battery_adapter)?.label || site.battery_adapter}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(site)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          site.active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {site.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Link
                        to={`/site/${site.id}`}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-3"
                      >
                        View
                      </Link>
                      <button
                        onClick={() => handleEdit(site)}
                        className="text-brand-orange hover:text-brand-orange-dark mr-3"
                      >
                        <Pencil className="w-4 h-4 inline" />
                      </button>
                      {deleteConfirm === site.id ? (
                        <span className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(site.id)}
                            disabled={deleting}
                            className="text-red-600 hover:text-red-700"
                          >
                            {deleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(site.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredSites.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
            itemLabel="sites"
          />
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="card shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingSite ? 'Edit Site' : 'Create Site'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto"
              autoComplete="off"
              onSubmit={(e) => e.preventDefault()}
            >
              {/* Owner (only for create) */}
              {!editingSite && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Owner
                  </label>
                  <select
                    value={formData.user_id || ''}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  >
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Site Name
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  placeholder="My Home"
                />
              </div>

              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Region
                </label>
                <select
                  value={formData.region || ''}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                >
                  {REGIONS.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>

              {/* Address Line 1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.address_line1 || ''}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  placeholder="123 Solar Street"
                />
              </div>

              {/* Address Line 2 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Address Line 2 <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.address_line2 || ''}
                  onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  placeholder="Unit 1, Building A"
                />
              </div>

              {/* City and Postcode Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City / Suburb
                  </label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    placeholder="Sydney"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Postcode
                  </label>
                  <input
                    type="text"
                    value={formData.postcode || ''}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    placeholder="2000"
                  />
                </div>
              </div>

              {/* Optimizer Engine - now visible (was buried under Advanced) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Optimizer Engine
                </label>
                <select
                  value={formData.optimizer_engine || ''}
                  onChange={(e) => setFormData({ ...formData, optimizer_engine: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                >
                  {OPTIMIZER_ENGINES.map((engine) => (
                    <option key={engine.value} value={engine.value}>
                      {engine.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Site active checkbox - now visible (was buried under Advanced). Edit only. */}
              {editingSite && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active || false}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 text-brand-orange border-gray-300 rounded focus:ring-brand-orange"
                  />
                  <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-300">
                    Site is active
                  </label>
                </div>
              )}

              {/* ============= Battery section ============= */}
              <SectionHeader
                title="Battery"
                icon={<BatteryIcon className="w-4 h-4" />}
                isOpen={openSection === 'battery'}
                onToggle={() => setOpenSection(openSection === 'battery' ? null : 'battery')}
              />
              {openSection === 'battery' && (
                <div className="space-y-4 pl-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Battery System
                    </label>
                    <select
                      value={formData.battery_adapter || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, battery_adapter: e.target.value, battery_credentials: {} })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {BATTERY_ADAPTERS.map((b) => (
                        <option key={b.value} value={b.value} disabled={b.comingSoon}>
                          {b.label}{b.comingSoon ? ' (Coming Soon)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.battery_adapter && BATTERY_ADAPTER_FIELDS[formData.battery_adapter]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {BATTERY_ADAPTERS.find((b) => b.value === formData.battery_adapter)?.label || formData.battery_adapter} Connection
                        {editingSite && <span className="ml-1">(leave blank to keep existing)</span>}
                      </p>
                      {BATTERY_ADAPTER_FIELDS[formData.battery_adapter].map((field) => (
                        <CredentialField
                          key={field.key}
                          field={field}
                          formNamespace="battery"
                          value={(formData.battery_credentials?.[field.key] as string) || ''}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              battery_credentials: {
                                ...formData.battery_credentials,
                                [field.key]: field.type === 'number' ? parseFloat(value) : value,
                              },
                            })
                          }
                          editing={!!editingSite}
                          revealed={revealedFields.has(`battery-${field.key}`)}
                          onToggleReveal={() => toggleRevealed(`battery-${field.key}`)}
                          credsLoading={credsLoading}
                          currentValue={getCurrentCredValue('battery', field.key)}
                          formatCredValue={formatCredValue}
                        />
                      ))}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Storage Capacity (kWh)
                    </label>
                    <input
                      type="number"
                      value={(formData.battery_config?.capacity_wh as number) / 1000 || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          battery_config: {
                            ...formData.battery_config,
                            capacity_wh: parseFloat(e.target.value) * 1000,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      placeholder="10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Grid Export Cap (kW, optional)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={(formData.battery_config?.max_export_w as number) / 1000 || ''}
                      onChange={(e) => {
                        const kw = parseFloat(e.target.value);
                        const { max_export_w: _omit, ...rest } = formData.battery_config || {};
                        setFormData({
                          ...formData,
                          battery_config: kw > 0 ? { ...rest, max_export_w: kw * 1000 } : rest,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      placeholder="Blank = battery discharge rating"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={Boolean(formData.battery_config?.ev_hold_via_soc_floor)}
                        onChange={(e) => {
                          const { ev_hold_via_soc_floor: _omit, ...rest } = formData.battery_config || {};
                          setFormData({
                            ...formData,
                            battery_config: e.target.checked ? { ...rest, ev_hold_via_soc_floor: true } : rest,
                          });
                        }}
                      />
                      <span>
                        EV hold via SOC floor (experimental)
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Regular Mode + raised on-grid discharge floor while an EV charges, instead of the Charge Mode hold.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* ============= Solar section ============= */}
              <SectionHeader
                title="Solar"
                icon={<SunIcon className="w-4 h-4" />}
                isOpen={openSection === 'solar'}
                onToggle={() => setOpenSection(openSection === 'solar' ? null : 'solar')}
              />
              {openSection === 'solar' && (
                <div className="space-y-4 pl-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Solar Capacity (kW)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={(formData.solar_config?.capacity_kw as number) || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          solar_config: { ...formData.solar_config, capacity_kw: parseFloat(e.target.value) },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      placeholder="6.6"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Solar Panel Brand
                    </label>
                    <input
                      type="text"
                      value={(formData.solar_config?.brand as string) || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          solar_config: { ...formData.solar_config, brand: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      placeholder="e.g. Sunpower, LG, REC"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Number of Panels
                    </label>
                    <input
                      type="number"
                      value={(formData.solar_config?.panels as number) || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          solar_config: { ...formData.solar_config, panels: parseInt(e.target.value) },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      placeholder="20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Inverter Model
                    </label>
                    <input
                      type="text"
                      value={(formData.solar_config?.inverter as string) || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          solar_config: { ...formData.solar_config, inverter: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                      placeholder="e.g. Fronius Primo 6.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Solar Inverter Control
                    </label>
                    <select
                      value={formData.solar_control_adapter || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          solar_control_adapter: e.target.value,
                          solar_credentials: {},
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {SOLAR_INVERTERS.map((inv) => (
                        <option key={inv.value} value={inv.value} disabled={inv.comingSoon}>
                          {inv.label}{inv.comingSoon ? ' (Coming Soon)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.solar_control_adapter && SOLAR_CONTROL_FIELDS[formData.solar_control_adapter]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Credentials {editingSite && <span>(leave blank to keep existing)</span>}
                      </p>
                      {SOLAR_CONTROL_FIELDS[formData.solar_control_adapter].map((field) => (
                        <CredentialField
                          key={field.key}
                          field={field}
                          formNamespace="solar"
                          value={(formData.solar_credentials?.[field.key] as string) || ''}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              solar_credentials: { ...formData.solar_credentials, [field.key]: value },
                            })
                          }
                          editing={!!editingSite}
                          revealed={revealedFields.has(`solar-${field.key}`)}
                          onToggleReveal={() => toggleRevealed(`solar-${field.key}`)}
                          credsLoading={credsLoading}
                          currentValue={getCurrentCredValue('solar', field.key)}
                          formatCredValue={formatCredValue}
                        />
                      ))}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Forecast Provider
                    </label>
                    <select
                      value={formData.forecast_provider || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          forecast_provider: e.target.value,
                          forecast_provider_credentials: {},
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {FORECAST_PROVIDERS.map((provider) => (
                        <option key={provider} value={provider}>
                          {provider === 'open_meteo' ? 'SolanIQ Forecaster (Free - No API key needed)' :
                           provider === 'forecast_solar' ? 'Forecast.Solar (Free - No API key needed)' :
                           provider === 'simulated' ? 'Simulated (Testing)' :
                           provider.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.forecast_provider && FORECAST_PROVIDER_FIELDS[formData.forecast_provider]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formData.forecast_provider.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} Credentials
                        {editingSite && <span className="ml-1">(leave blank to keep existing)</span>}
                      </p>
                      {FORECAST_PROVIDER_FIELDS[formData.forecast_provider].map((field) => (
                        <CredentialField
                          key={field.key}
                          field={field}
                          formNamespace="forecast"
                          value={(formData.forecast_provider_credentials?.[field.key] as string) || ''}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              forecast_provider_credentials: {
                                ...formData.forecast_provider_credentials,
                                [field.key]: value,
                              },
                            })
                          }
                          editing={!!editingSite}
                          revealed={revealedFields.has(`forecast-${field.key}`)}
                          onToggleReveal={() => toggleRevealed(`forecast-${field.key}`)}
                          credsLoading={credsLoading}
                          currentValue={getCurrentCredValue('forecast', field.key)}
                          formatCredValue={formatCredValue}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ============= Price section ============= */}
              <SectionHeader
                title="Price"
                icon={<DollarSignIcon className="w-4 h-4" />}
                isOpen={openSection === 'price'}
                onToggle={() => setOpenSection(openSection === 'price' ? null : 'price')}
              />
              {openSection === 'price' && (
                <div className="space-y-4 pl-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Price Provider
                    </label>
                    <select
                      value={formData.price_provider || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price_provider: e.target.value,
                          price_provider_credentials: {},
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    >
                      {PRICE_PROVIDERS.map((provider) => (
                        <option key={provider} value={provider}>
                          {provider === 'aemo' ? 'AEMO (Free - No API key needed)' :
                           provider === 'simulated' ? 'Simulated (Testing)' :
                           provider.charAt(0).toUpperCase() + provider.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.price_provider && PRICE_PROVIDER_FIELDS[formData.price_provider]?.length > 0 && (
                    <div className="ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-600 space-y-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formData.price_provider.charAt(0).toUpperCase() + formData.price_provider.slice(1)} Credentials
                        {editingSite && <span className="ml-1">(leave blank to keep existing)</span>}
                      </p>
                      {PRICE_PROVIDER_FIELDS[formData.price_provider].map((field) => (
                        <CredentialField
                          key={field.key}
                          field={field}
                          formNamespace="price"
                          value={(formData.price_provider_credentials?.[field.key] as string) || ''}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              price_provider_credentials: {
                                ...formData.price_provider_credentials,
                                [field.key]: value,
                              },
                            })
                          }
                          editing={!!editingSite}
                          revealed={revealedFields.has(`price-${field.key}`)}
                          onToggleReveal={() => toggleRevealed(`price-${field.key}`)}
                          credsLoading={credsLoading}
                          currentValue={getCurrentCredValue('price', field.key)}
                          formatCredValue={formatCredValue}
                        />
                      ))}
                    </div>
                  )}

                  {isUsingSimulation() && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Simulation Mode Active</span>
                      </div>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        This site uses simulated providers for testing. Select a scenario to control the simulation behavior.
                      </p>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Simulation Scenario
                        </label>
                        <select
                          value={formData.simulation_config?.scenario || 'sunny_day_tou'}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              simulation_config: { ...formData.simulation_config, scenario: e.target.value },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                        >
                          {SIMULATION_SCENARIOS.map((scenario) => (
                            <option key={scenario.id} value={scenario.id}>
                              {scenario.name}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {SIMULATION_SCENARIOS.find((s) => s.id === (formData.simulation_config?.scenario || 'sunny_day_tou'))?.description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </form>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name || (!editingSite && !formData.user_id)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingSite ? (
                  'Save Changes'
                ) : (
                  'Create Site'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
